import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { MediaCan, MediaKind, MediaLibrary, MediaResource } from '../../shared/api/types';
import { Alert, Button, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { inDesktopApp } from '../presentation/screens';
import { AppDownloadCard } from './AppDownloadCard';
import { LocalFileCard, LocalFilesCard, useLocalFiles } from './LocalFilesCard';
import { isEwThumbnail } from '../presentation/local';

const KINDS: { key: MediaKind | 'all'; label: string }[] = [
  { key: 'all', label: 'Todo' },
  { key: 'image', label: 'Imágenes' },
  { key: 'audio', label: 'Audio' },
  { key: 'video', label: 'Video' },
  { key: 'document', label: 'Documentos' },
];

/** Lo que el selector de archivos ofrece; el servidor vuelve a comprobar el tipo real. */
const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,audio/mpeg,audio/mp4,audio/x-m4a,audio/ogg,audio/wav,video/mp4,video/webm,application/pdf';

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1_048_576) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1_048_576).toFixed(n < 10_485_760 ? 1 : 0)} MB`;
}

export const mediaFileUrl = (churchId: string, id: string) => `/api/media/churches/${churchId}/resources/${id}/file`;

/** Motor 16, Recursos Multimedia: la biblioteca de imágenes, audio, video y documentos de la iglesia. */
export function MediaPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const last = useRef<MediaLibrary | null>(null);
  const managesAll = me.can('media.manage');
  const [params, setParams] = useSearchParams();
  const kind = (KINDS.find((k) => k.key === params.get('tipo'))?.key ?? 'all') as MediaKind | 'all';
  // Lo archivado lo ve quien puede eliminar (para restaurarlo).
  const archived = (managesAll || last.current?.can?.delete === true) && params.get('vista') === 'archivados';
  const [version, setVersion] = useState(0);
  const library = useAsync(
    (signal) =>
      churchId
        ? api.get<MediaLibrary>(`/media/churches/${churchId}/resources?archived=${archived}${kind === 'all' ? '' : `&kind=${kind}`}`, signal)
        : Promise.reject(new Error('Sin contexto')),
    [churchId, kind, archived, version],
  );
  if (library.status === 'ready') last.current = library.data;
  const data = library.status === 'ready' ? library.data : last.current;
  // Quien administra Multimedia puede todo; un integrante, lo que su líder le permitió (lo dice la biblioteca).
  const can: MediaCan = managesAll ? { upload: true, edit: true, delete: true } : (data?.can ?? { upload: false, edit: false, delete: false });
  const refresh = () => setVersion((v) => v + 1);
  // Carpetas (p. ej. el material de Predicación de cada culto).
  const folders = [...new Set((data?.items ?? []).map((r) => r.folder).filter((f): f is string => Boolean(f)))].sort((a, b) => b.localeCompare(a, 'es'));
  const folder = params.get('carpeta');
  const shown = (data?.items ?? []).filter((r) => !folder || r.folder === folder);
  // Dentro de la app, los archivos de esta computadora se acomodan en la misma Biblioteca (no en carpetas ni archivados).
  const localFiles = useLocalFiles();
  const localShown = archived || folder || kind === 'document' ? [] : (localFiles.files ?? []).filter((f) => !isEwThumbnail(f.name) && (kind === 'all' || f.kind === kind));
  const set = (next: Record<string, string | null>) => {
    const out = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) (v ? out.set(k, v) : out.delete(k));
    setParams(out);
  };

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;

  return (
    <div className="page">
      <header className="page__header">
        <h1>Multimedia</h1>
        <p className="muted">Imágenes, audio, video y documentos de la iglesia, listos para usar en cultos y publicaciones.</p>
      </header>
      {/* En la app de Windows: guardar en esta computadora o subir a la nube de la iglesia (#64). */}
      {inDesktopApp() ? <LocalFilesCard state={localFiles} /> : null}
      {can.delete ? (
        <div className="tabs" role="tablist" aria-label="Archivos">
          <button type="button" role="tab" aria-selected={!archived} className={`tabs__tab${!archived ? ' tabs__tab--active' : ''}`} onClick={() => set({ vista: null })}>
            Archivos
          </button>
          <button type="button" role="tab" aria-selected={archived} className={`tabs__tab${archived ? ' tabs__tab--active' : ''}`} onClick={() => set({ vista: 'archivados' })}>
            Archivados
          </button>
        </div>
      ) : null}
      {can.upload && !archived ? <Uploader churchId={churchId} maxBytes={data?.maxUploadBytes ?? null} onUploaded={refresh} label={inDesktopApp() ? 'Subir a la nube de la iglesia' : 'Subir archivos'} /> : null}
      <div className="chips" role="group" aria-label="Tipo">
        {KINDS.map((k) => (
          <button key={k.key} type="button" className={`chip${kind === k.key ? ' chip--active' : ''}`} aria-pressed={kind === k.key} onClick={() => set({ tipo: k.key === 'all' ? null : k.key })}>
            {k.label}
          </button>
        ))}
      </div>
      {folders.length ? (
        <div className="field media-folder">
          <label htmlFor="media-folder">Carpeta</label>
          <select id="media-folder" value={folder ?? ''} onChange={(e) => set({ carpeta: e.target.value || null })}>
            <option value="">Todas</option>
            {folders.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      {library.status === 'loading' && !data ? <Loading /> : null}
      {library.status === 'error' ? <ErrorState error={library.error} onRetry={library.reload} /> : null}
      {data && shown.length === 0 && localShown.length === 0 ? <EmptyState title={archived ? 'No hay archivos archivados.' : 'Aún no hay archivos aquí.'} /> : null}
      {data && shown.length + localShown.length > 0 ? (
        <ul className="media-grid">
          {shown.map((r) => (
            <ResourceCard key={r.id} churchId={churchId} resource={r} can={can} onChange={refresh} />
          ))}
          {localShown.map((f) => (
            <LocalFileCard key={`local:${f.name}`} file={f} state={localFiles} />
          ))}
        </ul>
      ) : null}
      {data && can.upload ? <p className="muted small">Espacio usado por la iglesia: {formatBytes(data.usedBytes)}.</p> : null}
      {/* Al final y en pequeño; dentro de la app ya instalada no hace falta ofrecerla y en el celular no se muestra (#63). */}
      {inDesktopApp() ? null : <AppDownloadCard />}
    </div>
  );
}

function Uploader({ churchId, maxBytes, onUploaded, label }: { churchId: string; maxBytes: number | null; onUploaded: () => void; label: string }) {
  const [progress, setProgress] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [done, setDone] = useState<string | null>(null);

  async function pick(event: ChangeEvent<HTMLInputElement>) {
    const input = event.currentTarget;
    const files = [...(input.files ?? [])];
    input.value = '';
    if (files.length === 0) return;
    const failed: string[] = [];
    let ok = 0;
    setErrors([]);
    setDone(null);
    for (const [i, file] of files.entries()) {
      setProgress(files.length > 1 ? `Subiendo ${i + 1} de ${files.length}: ${file.name}` : `Subiendo ${file.name}`);
      if (maxBytes !== null && file.size > maxBytes) {
        failed.push(`${file.name}: supera el límite de ${formatBytes(maxBytes)}.`);
        continue;
      }
      try {
        await api.upload(`/media/churches/${churchId}/resources`, file);
        ok++;
      } catch (e) {
        failed.push(`${file.name}: ${errorMessage(e)}`);
      }
    }
    setProgress(null);
    setErrors(failed);
    if (ok > 0) {
      setDone(ok === 1 ? 'Archivo subido.' : `${ok} archivos subidos.`);
      onUploaded();
    }
  }

  return (
    <div className="uploader">
      <label className={`btn btn--primary${progress ? ' btn--busy' : ''}`}>
        <input type="file" className="visually-hidden" accept={ACCEPT} multiple disabled={progress !== null} onChange={pick} />
        <span>{progress ? 'Subiendo…' : label}</span>
      </label>
      <p className="muted small" aria-live="polite">
        {progress ?? `JPG, PNG, GIF, WebP, MP3, M4A, OGG, WAV, MP4, WebM o PDF${maxBytes ? `, hasta ${formatBytes(maxBytes)} cada uno` : ''}.`}
      </p>
      {done ? <Alert tone="success">{done}</Alert> : null}
      {errors.length > 0 ? (
        <Alert>
          {errors.map((e) => (
            <span key={e} className="block">
              {e}
            </span>
          ))}
        </Alert>
      ) : null}
    </div>
  );
}

function Preview({ churchId, resource: r }: { churchId: string; resource: MediaResource }) {
  const src = mediaFileUrl(churchId, r.id);
  switch (r.kind) {
    case 'image':
      return <img className="media-card__preview" src={src} alt={r.name} loading="lazy" />;
    case 'audio':
      return <audio className="media-card__audio" src={src} controls preload="none" aria-label={r.name} />;
    case 'video':
      return <video className="media-card__preview" src={src} controls preload="metadata" aria-label={r.name} />;
    case 'document':
      return (
        <a className="media-card__preview media-card__doc" href={src} download>
          PDF
        </a>
      );
  }
}

function ResourceCard({ churchId, resource: r, can, onChange }: { churchId: string; resource: MediaResource; can: MediaCan; onChange: () => void }) {
  const [renaming, setRenaming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const base = `/media/churches/${churchId}/resources/${r.id}`;

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      setRenaming(false);
      onChange();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  function rename(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const name = String(new FormData(event.currentTarget).get('name') ?? '').trim();
    if (!name) return setError('Escribe un nombre.');
    void run(() => api.put(base, { name }));
  }

  return (
    <li className="media-card card">
      <Preview churchId={churchId} resource={r} />
      {renaming ? (
        <form className="form" onSubmit={rename} noValidate>
          <label className="visually-hidden" htmlFor={`name-${r.id}`}>
            Nombre
          </label>
          <input id={`name-${r.id}`} name="name" defaultValue={r.name} maxLength={160} autoFocus />
          <div className="actions">
            <Button type="submit" busy={busy}>
              Guardar
            </Button>
            <Button type="button" variant="ghost" onClick={() => setRenaming(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      ) : (
        <div className="media-card__body">
          <strong className="media-card__name">{r.name}</strong>
          <span className="muted small">
            {formatBytes(r.sizeBytes)} · {r.uploadedBy.name}
            {r.pages ? ` · ${r.pages} ${r.pages === 1 ? 'página' : 'páginas'}` : ''}
          </span>
          {r.folder ? <span className="small media-card__folder">📁 {r.folder}</span> : null}
          {inDesktopApp() ? <span className="small media-card__folder">☁ En la nube de la iglesia</span> : null}
        </div>
      )}
      {!renaming ? (
        <div className="actions actions--wrap">
          <a className="btn btn--ghost" href={mediaFileUrl(churchId, r.id)} download>
            <span>Descargar</span>
          </a>
          {can.edit && !r.archived ? (
            <Button variant="ghost" onClick={() => setRenaming(true)}>
              Renombrar
            </Button>
          ) : null}
          {can.delete ? (
            <Button variant="ghost" busy={busy} onClick={() => run(() => api.post(`${base}/${r.archived ? 'restore' : 'archive'}`))}>
              {r.archived ? 'Restaurar' : 'Archivar'}
            </Button>
          ) : null}
        </div>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
    </li>
  );
}
